import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson, apiSend } from "@/api/client";
import {
  segment,
  withQuery,
  type ArchiveVariables,
  type SaveVariables,
  type Versioned,
} from "@/api/query";

/* Mirrors api/src/pbo_invoicing/clients/schemas.py. Money is a string ("350.00"). */

export type RateGroup = "A" | "B";

export type ClientFields = {
  legal_name: string;
  address_lines: string[];
  country_code: string;
  cr_no: string;
  vat_no: string;
  place_of_supply: string;
  payment_days: number;
  currency: string;
  agreement_ref: string;
  agreement_effective_date: string | null;
  notes: string;
  reminders_enabled: boolean;
};

export type ClientCreateInput = ClientFields & { code: string };

export type Client = ClientCreateInput & Versioned & { archived_at: string | null };

export type ClientSummary = {
  id: string;
  code: string;
  legal_name: string;
  country_code: string;
  currency: string;
  payment_days: number;
  archived_at: string | null;
  version: number;
};

export type ContactInput = {
  name: string;
  title: string;
  email: string;
  phone: string;
  receives_invoices: boolean;
  is_primary: boolean;
};

export type Contact = ContactInput & Versioned & { client_id: string; archived_at: string | null };

export type RateItemInput = {
  item_no: number;
  code: string;
  rate_group: RateGroup;
  description: string;
  detail: string;
  unit_rate: string;
  /** null = use the client's currency */
  currency: string | null;
  auto_include: boolean;
  sort_order: number;
};

export type RateItem = Omit<RateItemInput, "currency"> &
  Versioned & { currency: string; client_id: string; archived_at: string | null };

export const clientKeys = {
  all: ["clients"] as const,
  lists: ["clients", "list"] as const,
  list: (q: string, includeArchived: boolean) =>
    ["clients", "list", { q, includeArchived }] as const,
  detail: (id: string) => ["clients", "detail", id] as const,
  contacts: (clientId: string) => ["clients", "contacts", clientId] as const,
  contactList: (clientId: string, includeArchived: boolean) =>
    ["clients", "contacts", clientId, { includeArchived }] as const,
  rateItems: (clientId: string) => ["clients", "rate-items", clientId] as const,
  rateItemList: (clientId: string, includeArchived: boolean) =>
    ["clients", "rate-items", clientId, { includeArchived }] as const,
};

function archivePath(base: string, id: string, archived: boolean): string {
  return `${base}/${segment(id)}/${archived ? "archive" : "unarchive"}`;
}

// --------------------------------------------------------------------------- requests

export function listClients(q = "", includeArchived = false): Promise<ClientSummary[]> {
  return apiJson<ClientSummary[]>(withQuery("/clients", { q, include_archived: includeArchived }));
}

export function createClient(input: ClientCreateInput): Promise<Client> {
  return apiSend<Client>("/clients", "POST", input);
}

export function getClient(id: string): Promise<Client> {
  return apiJson<Client>(`/clients/${segment(id)}`);
}

export function updateClient(id: string, input: ClientFields, version: number): Promise<Client> {
  return apiSend<Client>(`/clients/${segment(id)}`, "PUT", input, version);
}

export function setClientArchived(id: string, archived: boolean, version: number): Promise<Client> {
  return apiSend<Client>(archivePath("/clients", id, archived), "POST", undefined, version);
}

export function listContacts(clientId: string, includeArchived = false): Promise<Contact[]> {
  return apiJson<Contact[]>(
    withQuery(`/clients/${segment(clientId)}/contacts`, { include_archived: includeArchived }),
  );
}

export function createContact(clientId: string, input: ContactInput): Promise<Contact> {
  return apiSend<Contact>(`/clients/${segment(clientId)}/contacts`, "POST", input);
}

export function updateContact(id: string, input: ContactInput, version: number): Promise<Contact> {
  return apiSend<Contact>(`/contacts/${segment(id)}`, "PUT", input, version);
}

export function setContactArchived(
  id: string,
  archived: boolean,
  version: number,
): Promise<Contact> {
  return apiSend<Contact>(archivePath("/contacts", id, archived), "POST", undefined, version);
}

export function listRateItems(clientId: string, includeArchived = false): Promise<RateItem[]> {
  return apiJson<RateItem[]>(
    withQuery(`/clients/${segment(clientId)}/rate-items`, { include_archived: includeArchived }),
  );
}

export function createRateItem(clientId: string, input: RateItemInput): Promise<RateItem> {
  return apiSend<RateItem>(`/clients/${segment(clientId)}/rate-items`, "POST", input);
}

export function updateRateItem(
  id: string,
  input: RateItemInput,
  version: number,
): Promise<RateItem> {
  return apiSend<RateItem>(`/rate-items/${segment(id)}`, "PUT", input, version);
}

export function setRateItemArchived(
  id: string,
  archived: boolean,
  version: number,
): Promise<RateItem> {
  return apiSend<RateItem>(archivePath("/rate-items", id, archived), "POST", undefined, version);
}

// --------------------------------------------------------------------------- hooks

export function useClients(q: string, includeArchived: boolean) {
  return useQuery({
    queryKey: clientKeys.list(q, includeArchived),
    queryFn: () => listClients(q, includeArchived),
  });
}

export function useClient(id: string) {
  return useQuery({ queryKey: clientKeys.detail(id), queryFn: () => getClient(id) });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createClient,
    onSuccess: (client) => {
      queryClient.setQueryData(clientKeys.detail(client.id), client);
      return queryClient.invalidateQueries({ queryKey: clientKeys.lists });
    },
  });
}

export function useUpdateClient(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, version }: { input: ClientFields; version: number }) =>
      updateClient(id, input, version),
    onSuccess: (client) => {
      queryClient.setQueryData(clientKeys.detail(id), client);
      return queryClient.invalidateQueries({ queryKey: clientKeys.lists });
    },
  });
}

export function useArchiveClient(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ archived, version }: { archived: boolean; version: number }) =>
      setClientArchived(id, archived, version),
    onSuccess: (client) => {
      queryClient.setQueryData(clientKeys.detail(id), client);
      return queryClient.invalidateQueries({ queryKey: clientKeys.lists });
    },
  });
}

export function useContacts(clientId: string, includeArchived: boolean) {
  return useQuery({
    queryKey: clientKeys.contactList(clientId, includeArchived),
    queryFn: () => listContacts(clientId, includeArchived),
  });
}

export function useSaveContact(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, existing }: SaveVariables<ContactInput>) =>
      existing
        ? updateContact(existing.id, input, existing.version)
        : createContact(clientId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clientKeys.contacts(clientId) }),
  });
}

export function useArchiveContact(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived, version }: ArchiveVariables) =>
      setContactArchived(id, archived, version),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clientKeys.contacts(clientId) }),
  });
}

export function useRateItems(clientId: string, includeArchived: boolean) {
  return useQuery({
    queryKey: clientKeys.rateItemList(clientId, includeArchived),
    queryFn: () => listRateItems(clientId, includeArchived),
  });
}

export function useSaveRateItem(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, existing }: SaveVariables<RateItemInput>) =>
      existing
        ? updateRateItem(existing.id, input, existing.version)
        : createRateItem(clientId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clientKeys.rateItems(clientId) }),
  });
}

export function useArchiveRateItem(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived, version }: ArchiveVariables) =>
      setRateItemArchived(id, archived, version),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clientKeys.rateItems(clientId) }),
  });
}
